import { useState } from "react";
import { Link } from "react-router-dom";
import {
    Star,
    ShieldCheck,
    DollarSign,
    Sparkles,
    Users,
    Share2,
    ArrowRight,
    CheckCircle2,
    Check,
    Mic,
} from "lucide-react";
import WaitlistForm from "../components/WaitlistForm";
import FAQ from "../components/FAQ";
import Reveal from "../components/Reveal";
import PhoneShowcase from "../components/PhoneShowcase";
import { Dialog, DialogContent, DialogTitle } from "../components/ui/dialog";

const IMG = {
    voice: "/images/voice.png",
    home: "/images/home.png",
    booking: "/images/booking.png",
    profile: "/images/profile.png",
    refHowItWorks:
        "https://customer-assets.emergentagent.com/job_dmv-mechanics-hub/artifacts/8e9yu4ll_image.png",
};

function Eyebrow({ children, light }) {
    return (
        <div
            className={`inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] font-bold ${
                light ? "text-[#34D399]" : "text-[#10B981]"
            }`}
            data-testid="eyebrow"
        >
            <span
                className={`w-6 h-px ${
                    light ? "bg-[#34D399]" : "bg-[#10B981]"
                }`}
            />
            {children}
        </div>
    );
}

/* ---------------------- HERO ---------------------- */
function Hero() {
    return (
        <section
            id="hero"
            data-testid="hero-section"
            className="relative pt-44 sm:pt-48 pb-20 sm:pb-24 overflow-hidden mx-mint-gradient"
        >
            {/* Decorative grid */}
            <div className="absolute inset-0 mx-grid-bg opacity-60 pointer-events-none" />
            <div className="absolute top-40 -right-24 w-[520px] h-[520px] rounded-full bg-[#10B981]/10 blur-3xl pointer-events-none" />

            <div className="relative max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-12 gap-12 items-center">
                <div className="lg:col-span-7">
                    <Reveal>
                        <Eyebrow>
                            DMV — DC · MD · VA · Waitlist open
                        </Eyebrow>
                    </Reveal>
                    <Reveal delay={0.05}>
                        <h1
                            data-testid="hero-headline"
                            className="mt-5 text-4xl sm:text-6xl lg:text-7xl font-black leading-[1.02] tracking-tighter text-[#070F1D]"
                        >
                            Find a trusted{" "}
                            <span className="relative inline-block">
                                <span className="relative z-10 text-[#10B981]">
                                    mechanic
                                </span>
                                <span className="absolute left-0 right-0 bottom-1 h-3 bg-[#10B981]/25 -z-0 blur-xl" />
                            </span>{" "}
                            in minutes
                            <br className="hidden sm:block" /> — or just{" "}
                            <span className="italic font-light text-slate-700">
                                describe
                            </span>{" "}
                            your problem.
                        </h1>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <p className="mt-6 text-lg sm:text-xl text-slate-600 max-w-xl leading-relaxed">
                            Tap the mic. Say what&apos;s wrong. MechXpress matches
                            you to a verified local mechanic with upfront
                            pricing — no dealer markup, no runaround.
                        </p>
                    </Reveal>

                    <Reveal delay={0.15}>
                        <div className="mt-8 flex flex-wrap items-center gap-3 text-sm text-slate-700">
                            {[
                                { icon: ShieldCheck, t: "Verified mechanics" },
                                { icon: DollarSign, t: "Upfront pricing" },
                                { icon: Star, t: "3★ minimum rating" },
                            ].map(({ icon: Icon, t }) => (
                                <div
                                    key={t}
                                    className="inline-flex items-center gap-2 rounded-full bg-white border border-slate-200 px-4 py-2 shadow-sm"
                                >
                                    <Icon className="w-4 h-4 text-[#10B981]" />
                                    <span className="font-semibold">
                                        {t}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </Reveal>
                    <Reveal delay={0.2}>
                        <Link
                            to="/fix-or-replace"
                            data-testid="hero-calc-pill"
                            className="mt-5 inline-flex items-center gap-3 rounded-full bg-[#0C447C] hover:bg-[#0a3a6b] text-white font-bold pl-2 pr-5 py-2 shadow-[0_12px_24px_-10px_rgba(12,68,124,0.55)] transition-all group"
                        >
                            <span className="inline-flex items-center gap-1.5 rounded-full bg-[#10B981] text-white px-3 py-1 text-[11px] uppercase tracking-wider">
                                <Sparkles className="w-3 h-3" />
                                New
                            </span>
                            <span>
                                Should you repair or replace? Take the
                                30-second calculator
                            </span>
                            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </Link>
                    </Reveal>
                </div>

                <div className="lg:col-span-5">
                    <Reveal delay={0.2}>
                        <div className="relative">
                            <div className="absolute -top-6 left-8 right-8 h-12 bg-[#10B981]/30 blur-2xl rounded-full" />
                            <div className="relative">
                                <div className="mb-3 text-xs uppercase tracking-[0.22em] text-slate-500 font-bold">
                                    Join the waitlist
                                </div>
                                <WaitlistForm defaultTab="customer" />
                            </div>
                        </div>
                    </Reveal>
                </div>
            </div>
        </section>
    );
}

/* ---------------------- HOW IT WORKS ---------------------- */
const STEPS = [
    {
        img: IMG.voice,
        alt: "Voice match screen",
        title: "Voice search.",
        desc: "Speak or type your car issue — no jargon needed.",
    },
    {
        img: IMG.home,
        alt: "Matched mechanics screen",
        title: "Mechanic matched.",
        desc: "Get matched with verified, local mechanics fast.",
    },
    {
        img: IMG.booking,
        alt: "Booking screen",
        title: "Book in minutes.",
        desc: "See prices upfront, then confirm and relax.",
    },
];

function HowItWorks() {
    return (
        <section
            id="how-it-works"
            data-testid="how-it-works-section"
            className="relative py-24 sm:py-32 bg-emerald-50/60"
        >
            <div className="absolute inset-0 mx-grid-bg opacity-30 pointer-events-none" />
            <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
                <div className="text-center mb-16">
                    <Reveal>
                        <Eyebrow>How it works</Eyebrow>
                    </Reveal>
                    <Reveal delay={0.05}>
                        <h2 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-[#070F1D]">
                            Three taps, one{" "}
                            <span className="text-[#10B981]">fix.</span>
                        </h2>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <p className="mt-5 text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed">
                            No forms. No phone tag. Just describe the problem
                            and we handle the rest — from match to mechanic
                            at your door.
                        </p>
                    </Reveal>
                </div>

                <PhoneShowcase steps={STEPS} />

                <Reveal delay={0.2}>
                    <div className="mt-16 flex items-center justify-center gap-3 text-sm text-slate-500">
                        <Mic className="w-4 h-4 text-[#10B981]" />
                        <span>
                            Tap any screen above to preview — this is the
                            actual app.
                        </span>
                    </div>
                </Reveal>
            </div>
        </section>
    );
}

/* ---------------------- EARNINGS / FOR MECHANICS TEASER ---------------------- */
function EarningsBlock() {
    return (
        <section
            id="earnings"
            data-testid="earnings-section"
            className="relative py-24 sm:py-32 bg-white"
        >
            <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-12 gap-12 items-center">
                <div className="lg:col-span-5">
                    <Reveal>
                        <Eyebrow>For mechanics</Eyebrow>
                    </Reveal>
                    <Reveal delay={0.05}>
                        <h2 className="mt-4 text-4xl sm:text-5xl font-black tracking-tight leading-tight text-[#070F1D]">
                            Your customers.
                            <br />
                            <span className="text-[#10B981]">
                                Your schedule. Your prices.
                            </span>
                        </h2>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-lg">
                            MechXpress gives independent mechanics a network to
                            grow from — without handing over control. Set your
                            rates, pick the jobs that fit, and keep building
                            the customer relationships you earned.
                        </p>
                    </Reveal>
                    <Reveal delay={0.15}>
                        <Link
                            to="/for-mechanics"
                            data-testid="earnings-cta"
                            className="mt-8 inline-flex items-center gap-2 text-[#10B981] hover:text-[#059669] font-bold group"
                        >
                            Learn more about joining as a mechanic
                            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </Link>
                    </Reveal>
                </div>

                <div className="lg:col-span-7">
                    <Reveal delay={0.15}>
                        <div
                            data-testid="earnings-card"
                            className="relative rounded-3xl overflow-hidden border border-slate-200 bg-gradient-to-br from-emerald-50 via-white to-emerald-50 shadow-[0_24px_60px_-20px_rgba(16,185,129,0.2)]"
                        >
                            <div className="absolute -top-20 -right-20 w-80 h-80 bg-[#10B981]/15 blur-3xl rounded-full pointer-events-none" />
                            <div className="relative p-8 sm:p-12">
                                <div className="text-xs uppercase tracking-[0.2em] text-[#10B981] font-bold">
                                    Built around the mechanic
                                </div>
                                <h3 className="mt-4 text-3xl sm:text-4xl font-black tracking-tight leading-tight text-[#070F1D] max-w-md">
                                    The network works for you —{" "}
                                    <span className="text-[#10B981]">
                                        not the other way around.
                                    </span>
                                </h3>
                                <div className="mt-10 grid sm:grid-cols-3 gap-3">
                                    {[
                                        "Set your own prices",
                                        "Own your customer list",
                                        "Pick your own hours",
                                    ].map((t) => (
                                        <div
                                            key={t}
                                            className="flex items-center gap-2 text-sm text-[#070F1D] bg-white border border-slate-200 rounded-full px-4 py-3 shadow-sm"
                                        >
                                            <CheckCircle2 className="w-4 h-4 text-[#10B981] shrink-0" />
                                            <span className="font-semibold">
                                                {t}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </Reveal>
                </div>
            </div>
        </section>
    );
}

/* ---------------------- TRUST ---------------------- */
const TRUST = [
    {
        icon: ShieldCheck,
        title: "Independently verified",
        desc: "Every mechanic is vetted for credentials, experience, word of mouth (2–3 colleagues vouching), and past work — before they see a single job.",
    },
    {
        icon: Star,
        title: "Ratings that mean something",
        desc: "3-star minimum rating, enforced automatically. Repeat low reviews = off the platform.",
    },
    {
        icon: DollarSign,
        title: "Upfront pricing, always",
        desc: "The price you see is the price you pay. No surprise fees at job completion. Ever.",
    },
];

function TrustSection() {
    const [profileOpen, setProfileOpen] = useState(false);
    return (
        <section
            id="trust"
            data-testid="trust-section"
            className="relative py-24 sm:py-32 bg-slate-50 border-y border-slate-200"
        >
            <div className="max-w-7xl mx-auto px-6 lg:px-8">
                <div className="max-w-2xl">
                    <Reveal>
                        <Eyebrow>Trust, by design</Eyebrow>
                    </Reveal>
                    <Reveal delay={0.05}>
                        <h2 className="mt-4 text-4xl sm:text-5xl font-black tracking-tight text-[#070F1D]">
                            No shady quotes.
                            <br />
                            No mystery fees.
                        </h2>
                    </Reveal>
                </div>

                <div className="mt-16 grid md:grid-cols-3 gap-6">
                    {TRUST.map((t, i) => (
                        <Reveal key={t.title} delay={i * 0.08}>
                            <div
                                data-testid={`trust-card-${i}`}
                                className="h-full bg-white border border-slate-200 rounded-2xl p-8 hover:border-[#10B981]/40 hover:-translate-y-1 hover:shadow-[0_20px_40px_-20px_rgba(16,185,129,0.25)] transition-all duration-500"
                            >
                                <div className="w-12 h-12 rounded-xl bg-emerald-50 border border-emerald-100 grid place-items-center mb-6">
                                    <t.icon className="w-6 h-6 text-[#10B981]" />
                                </div>
                                <h3 className="text-xl font-bold text-[#070F1D]">
                                    {t.title}
                                </h3>
                                <p className="mt-3 text-slate-600 leading-relaxed">
                                    {t.desc}
                                </p>
                            </div>
                        </Reveal>
                    ))}
                </div>

                {/* Verified Pro proof — Profile screenshot */}
                <Reveal delay={0.2}>
                    <div
                        data-testid="verified-proof"
                        className="mt-20 grid lg:grid-cols-12 gap-10 items-center rounded-3xl border border-slate-200 bg-white p-8 sm:p-12 overflow-hidden relative shadow-[0_20px_60px_-20px_rgba(7,15,29,0.1)]"
                    >
                        <div className="absolute -top-20 -right-20 w-80 h-80 bg-[#10B981]/10 blur-3xl rounded-full pointer-events-none" />
                        <div className="lg:col-span-7 relative">
                            <div className="inline-flex items-center gap-2 rounded-full bg-emerald-50 border border-emerald-200 px-3 py-1 text-xs font-bold uppercase tracking-wider text-[#059669]">
                                <ShieldCheck className="w-3.5 h-3.5" />
                                Verified Pro
                            </div>
                            <h3 className="mt-5 text-3xl sm:text-4xl font-black tracking-tight leading-tight text-[#070F1D]">
                                Every profile, independently
                                <br />
                                <span className="text-[#10B981]">
                                    verified & rated.
                                </span>
                            </h3>
                            <p className="mt-5 text-slate-600 leading-relaxed max-w-lg">
                                See credentials, years of experience, real
                                reviews, and exact service pricing before you
                                book. No guesswork. No bait-and-switch.
                            </p>
                            <ul className="mt-6 space-y-3 text-slate-700">
                                {[
                                    "Real customer reviews with photo verification",
                                    "Transparent service pricing list",
                                    "Live availability & response time",
                                ].map((t) => (
                                    <li
                                        key={t}
                                        className="flex items-start gap-3"
                                    >
                                        <CheckCircle2 className="w-5 h-5 mt-0.5 text-[#10B981] shrink-0" />
                                        <span>{t}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div className="lg:col-span-5 relative flex items-center justify-center">
                            <button
                                type="button"
                                onClick={() => setProfileOpen(true)}
                                data-testid="profile-screenshot-btn"
                                aria-label="View mechanic profile screen"
                                className="group relative focus:outline-none focus-visible:ring-2 focus-visible:ring-[#10B981] focus-visible:ring-offset-4 focus-visible:ring-offset-white rounded-[36px]"
                            >
                                <div className="absolute inset-x-0 bottom-0 h-48 bg-[#10B981]/0 group-hover:bg-[#10B981]/35 blur-3xl rounded-full pointer-events-none transition-colors duration-500" />
                                <img
                                    src={IMG.profile}
                                    alt="Verified mechanic profile"
                                    data-testid="profile-screenshot"
                                    className="relative z-10 max-h-[560px] object-contain mx-phone-shadow rounded-[36px] transition-all duration-500 group-hover:-translate-y-2 group-hover:drop-shadow-[0_40px_60px_rgba(16,185,129,0.3)]"
                                />
                                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 translate-y-2 opacity-0 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500 z-20">
                                    <span className="inline-flex items-center gap-1 rounded-full bg-[#070F1D] text-white text-[11px] font-bold tracking-wider uppercase px-3 py-1.5 shadow-lg">
                                        Tap to zoom
                                    </span>
                                </div>
                            </button>
                        </div>
                    </div>
                </Reveal>
            </div>

            <Dialog open={profileOpen} onOpenChange={setProfileOpen}>
                <DialogContent
                    data-testid="profile-lightbox"
                    className="max-w-[96vw] sm:max-w-xl md:max-w-2xl bg-transparent border-0 p-0 shadow-none [&>button]:text-white [&>button]:bg-black/40 [&>button]:rounded-full [&>button]:p-2 [&>button]:backdrop-blur-md [&>button]:border-0 [&>button]:hover:bg-black/60"
                >
                    <DialogTitle className="sr-only">
                        Mechanic profile preview
                    </DialogTitle>
                    <div className="relative flex items-center justify-center">
                        <div className="absolute inset-x-12 bottom-0 h-48 bg-[#10B981]/40 blur-3xl rounded-full pointer-events-none" />
                        <img
                            src={IMG.profile}
                            alt="Verified mechanic profile"
                            className="relative z-10 max-h-[86vh] w-auto object-contain rounded-[40px] drop-shadow-[0_40px_80px_rgba(0,0,0,0.5)]"
                        />
                    </div>
                    <div className="relative mt-4 text-center">
                        <div className="text-xs uppercase tracking-[0.2em] text-[#34D399] font-bold">
                            Verified Pro
                        </div>
                        <h3 className="mt-1 text-white font-black text-2xl">
                            Mechanic profile
                        </h3>
                    </div>
                </DialogContent>
            </Dialog>
        </section>
    );
}

/* ---------------------- STATS STRIP ---------------------- */
function StatsStrip() {
    const items = [
        { v: "DMV", l: "Launching first" },
        { v: "100%", l: "Customer ownership" },
        { v: "3★", l: "Minimum rating" },
        { v: "$0", l: "Hidden fees" },
    ];
    return (
        <section
            data-testid="stats-strip"
            className="relative border-y border-slate-200 py-10 bg-white"
        >
            <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-2 md:grid-cols-4 gap-6">
                {items.map((s, i) => (
                    <Reveal key={s.l} delay={i * 0.06}>
                        <div className="text-center md:text-left">
                            <div className="text-3xl sm:text-4xl font-black tracking-tight text-[#070F1D]">
                                {s.v}
                            </div>
                            <div className="mt-1 text-xs uppercase tracking-wider text-slate-500 font-semibold">
                                {s.l}
                            </div>
                        </div>
                    </Reveal>
                ))}
            </div>
        </section>
    );
}

/* ---------------------- MECHANICS TEASER (dark for contrast) ---------------------- */
const VALUE_PROPS = [
    {
        icon: Share2,
        title: "Referral network",
        desc: "Get overflow jobs from nearby mechanics who trust your specialty. No more turning away work that isn't your lane.",
    },
    {
        icon: Sparkles,
        title: "Specialization, not compromise",
        desc: "You don't need to learn hybrid diagnostics. Just refer it to someone who already has — and they'll do the same for you.",
    },
    {
        icon: Users,
        title: "Your business, your terms",
        desc: "Set your own prices, manage your own schedule, and keep your customer relationships. We just make it easier to get found.",
    },
];

function MechanicsTeaser() {
    return (
        <section
            id="mechanics-teaser"
            data-testid="mechanics-teaser"
            className="relative py-24 sm:py-32 bg-[#070F1D] text-white overflow-hidden"
        >
            <div className="absolute inset-0 mx-dark-gradient pointer-events-none" />
            <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
                <div className="grid lg:grid-cols-12 gap-10 items-end mb-14">
                    <div className="lg:col-span-7">
                        <Reveal>
                            <Eyebrow light>Built for mechanics</Eyebrow>
                        </Reveal>
                        <Reveal delay={0.05}>
                            <h2 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05] text-white">
                                Built for mechanics —
                                <br />
                                <span className="text-[#34D399]">
                                    not just customers.
                                </span>
                            </h2>
                        </Reveal>
                    </div>
                    <div className="lg:col-span-5">
                        <Reveal delay={0.1}>
                            <p className="text-lg text-slate-300 leading-relaxed">
                                MechXpress isn&apos;t just another lead gen app.
                                It&apos;s a network where independent mechanics grow
                                together — sharing referrals, covering
                                specialization gaps, and building real
                                business.
                            </p>
                        </Reveal>
                    </div>
                </div>

                <div className="grid md:grid-cols-3 gap-6">
                    {VALUE_PROPS.map((v, i) => (
                        <Reveal key={v.title} delay={i * 0.08}>
                            <div
                                data-testid={`value-prop-${i}`}
                                className="group h-full bg-white/[0.03] border border-white/10 rounded-2xl p-8 hover:border-[#10B981]/50 hover:bg-white/[0.06] hover:-translate-y-1 transition-all duration-500"
                            >
                                <div className="w-12 h-12 rounded-xl bg-[#10B981]/10 border border-[#10B981]/30 grid place-items-center mb-6 group-hover:bg-[#10B981] group-hover:border-[#10B981] transition-colors">
                                    <v.icon className="w-6 h-6 text-[#34D399] group-hover:text-[#070F1D] transition-colors" />
                                </div>
                                <h3 className="text-xl font-bold text-white">
                                    {v.title}
                                </h3>
                                <p className="mt-3 text-slate-300 leading-relaxed">
                                    {v.desc}
                                </p>
                            </div>
                        </Reveal>
                    ))}
                </div>

                <Reveal delay={0.25}>
                    <div className="mt-14 flex justify-center">
                        <Link
                            to="/for-mechanics"
                            data-testid="mechanics-learn-more"
                            className="inline-flex items-center gap-2 bg-[#10B981] hover:bg-[#34D399] text-[#070F1D] font-bold rounded-full px-8 py-4 shadow-[0_0_30px_rgba(16,185,129,0.35)] hover:shadow-[0_0_40px_rgba(52,211,153,0.55)] transition-all group"
                        >
                            Learn more about joining as a mechanic
                            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </Link>
                    </div>
                </Reveal>
            </div>
        </section>
    );
}

/* ---------------------- WAITLIST CTA ---------------------- */
function WaitlistCTA() {
    return (
        <section
            id="waitlist"
            data-testid="waitlist-section"
            className="relative py-24 sm:py-32 overflow-hidden bg-white"
        >
            <div className="absolute inset-0 mx-mint-gradient opacity-80 pointer-events-none" />
            <div className="max-w-5xl mx-auto px-6 lg:px-8 text-center relative">
                <Reveal>
                    <Eyebrow>Waitlist</Eyebrow>
                </Reveal>
                <Reveal delay={0.05}>
                    <h2 className="mt-4 text-4xl sm:text-6xl font-black tracking-tight leading-[1.05] text-[#070F1D]">
                        Get in early.
                        <br />
                        <span className="text-[#10B981]">
                            Get in first.
                        </span>
                    </h2>
                </Reveal>
                <Reveal delay={0.1}>
                    <p className="mt-5 text-lg text-slate-600 max-w-2xl mx-auto">
                        MechXpress is launching in the DMV area. Waitlist
                        members get first access, founding member perks, and
                        priority matching.
                    </p>
                </Reveal>
                <Reveal delay={0.15}>
                    <div className="mt-12 max-w-lg mx-auto">
                        <WaitlistForm defaultTab="customer" />
                    </div>
                </Reveal>
            </div>
        </section>
    );
}

/* ---------------- CALCULATOR CTA ---------------- */
function CalculatorCTA() {
    return (
        <section
            id="calculator-cta"
            data-testid="calculator-cta"
            className="relative py-20 sm:py-28 bg-white"
        >
            <div className="max-w-7xl mx-auto px-6 lg:px-8">
                <Reveal>
                    <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#0C447C] via-[#0a3a6b] to-[#0C447C] text-white p-8 sm:p-12 lg:p-16 grid lg:grid-cols-12 gap-10 items-center">
                        <div className="absolute -top-24 -right-24 w-[420px] h-[420px] rounded-full bg-[#10B981]/25 blur-3xl pointer-events-none" />
                        <div className="absolute inset-0 mx-grid-bg opacity-10 pointer-events-none" />

                        <div className="lg:col-span-7 relative">
                            <div className="inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur border border-white/15 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-[#34D399]">
                                <Sparkles className="w-3 h-3" />
                                Free tool · no signup
                            </div>
                            <h2 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[1.04]">
                                Should You Repair
                                <br />
                                <span className="text-[#34D399]">
                                    or Replace Your Car?
                                </span>
                            </h2>
                            <p className="mt-5 text-lg text-slate-200 leading-relaxed max-w-xl">
                                Got a big repair quote and not sure if it&apos;s
                                worth it? Run the numbers in 30 seconds.
                                We&apos;ll tell you whether repairing or
                                replacing makes more financial sense.
                            </p>
                            <Link
                                to="/fix-or-replace"
                                data-testid="calculator-cta-link"
                                className="mt-8 inline-flex items-center gap-2 bg-[#10B981] hover:bg-[#34D399] text-[#070F1D] font-bold rounded-full px-7 py-4 shadow-[0_16px_32px_-12px_rgba(16,185,129,0.55)] transition-all group"
                            >
                                Take the 30-second calculator
                                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                            </Link>
                            <div className="mt-5 flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-300">
                                <span className="inline-flex items-center gap-1.5">
                                    <Check className="w-4 h-4 text-[#34D399]" />
                                    5 questions
                                </span>
                                <span className="inline-flex items-center gap-1.5">
                                    <Check className="w-4 h-4 text-[#34D399]" />
                                    Shareable result
                                </span>
                                <span className="inline-flex items-center gap-1.5">
                                    <Check className="w-4 h-4 text-[#34D399]" />
                                    No upsell
                                </span>
                            </div>
                        </div>

                        <div className="lg:col-span-5 relative">
                            <div className="rounded-2xl bg-white/[0.06] border border-white/10 backdrop-blur p-6">
                                <div className="text-[11px] font-bold uppercase tracking-wider text-[#34D399]">
                                    Sample verdict
                                </div>
                                <div className="mt-3 text-2xl sm:text-3xl font-black tracking-tight text-white">
                                    &quot;Repair it — quote = 3.3 months of a car
                                    payment.&quot;
                                </div>
                                <div className="mt-5 grid grid-cols-3 gap-3">
                                    {[
                                        { l: "Repair", v: "$1,500" },
                                        { l: "Replace", v: "$8,500" },
                                        { l: "Past 12mo", v: "$300" },
                                    ].map((s) => (
                                        <div
                                            key={s.l}
                                            className="rounded-xl bg-white/[0.05] border border-white/10 px-3 py-2"
                                        >
                                            <div className="text-[10px] uppercase tracking-wider text-slate-400">
                                                {s.l}
                                            </div>
                                            <div className="font-black text-white">
                                                {s.v}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </Reveal>
            </div>
        </section>
    );
}

export default function Landing() {
    return (
        <main data-testid="landing-page" className="relative bg-white">
            <Hero />
            <CalculatorCTA />
            <HowItWorks />
            <EarningsBlock />
            <TrustSection />
            <StatsStrip />
            <MechanicsTeaser />
            <FAQ />
            <WaitlistCTA />
        </main>
    );
}
